import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Search, SlidersHorizontal, Users, X } from 'lucide-react';
import type { AdminContactsResponse, AdminContactListRow } from '@oracord/shared';
import { apiFetch, ApiError } from '../lib/api';
import { useSessionStore } from '../stores/session';
import { PageHeader, SectionCard, StatusBadge } from '../components/WebPresentation';
import {
  filterContactsForDirectory,
  summarizeContactsDirectory,
  type ContactDirectorySort,
  type ContactFollowUpFilter,
} from '../lib/contacts-directory';

// Phase 3C — Contacts directory.
//
// Every external phone this org has interacted with, newest-contact
// first. Derived server-side from the `calls` table (not from the
// `contacts` address book), so phones we've spoken with but never
// saved a contact row for still appear. Name/company come from the
// address book when we have it, null otherwise.
//
// Honesty policy:
//   - If we don't have a name, we show the phone and label "Unknown
//     contact" — never a made-up default.
//   - totalCalls / lastContactAt / pendingFollowUps
//     are all computed server-side from real rows. No heuristics.
//   - Search/filter/sort controls are client-side only over the loaded
//     contact rows. No fuzzy matching, scoring, or backend expansion.

const FOLLOW_UP_FILTERS: { key: ContactFollowUpFilter; label: string; help: string }[] = [
  { key: 'all', label: 'All contacts', help: 'No follow-up filter' },
  { key: 'open', label: 'Open follow-ups', help: 'Contacts with pending follow-ups' },
  { key: 'none', label: 'No open follow-ups', help: 'Contacts without pending follow-ups' },
];

const SORT_OPTIONS: { key: ContactDirectorySort; label: string }[] = [
  { key: 'default', label: 'Server order' },
  { key: 'newest', label: 'Newest first' },
  { key: 'oldest', label: 'Oldest first' },
  { key: 'open-followups', label: 'Open follow-ups first' },
  { key: 'calls-desc', label: 'Most calls' },
  { key: 'name', label: 'Name/company' },
];

export default function Contacts() {
  const activeOrgId = useSessionStore(s => s.activeOrganizationId);
  const [rows, setRows] = useState<AdminContactListRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [viewOptionsOpen, setViewOptionsOpen] = useState(false);
  const [followUpFilter, setFollowUpFilter] = useState<ContactFollowUpFilter>('all');
  const [sort, setSort] = useState<ContactDirectorySort>('default');

  const load = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<AdminContactsResponse>('/api/admin/contacts');
      setRows(res.rows);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : (err as Error).message || 'Failed to load contacts',
      );
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleRows = useMemo(
    () => filterContactsForDirectory(rows ?? [], {
      search,
      followUps: followUpFilter,
      sort,
    }),
    [rows, search, followUpFilter, sort],
  );
  const summary = useMemo(
    () => summarizeContactsDirectory(rows ?? [], visibleRows.length),
    [rows, visibleRows.length],
  );
  const hasFilters = search.trim() !== '' || followUpFilter !== 'all';
  const hasViewState = hasFilters || sort !== 'default';
  const showViewOptions = viewOptionsOpen || hasViewState;

  function clearFilters() {
    setSearch('');
    setFollowUpFilter('all');
    setSort('default');
    setSearchOpen(false);
    setViewOptionsOpen(false);
  }

  function closeSearch() {
    setSearch('');
    setSearchOpen(false);
  }

  if (!activeOrgId) {
    return <div className="text-sm text-slate-500">No organization selected.</div>;
  }

  return (
    <div className="max-w-6xl space-y-5">
      <PageHeader
        eyebrow="Customer memory"
        title="Contacts"
        description="A quiet phonebook Oracord grows from confirmed customer interactions."
        action={(
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm shadow-slate-200/50 transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      />

      <div className="rounded-2xl border border-slate-200/80 bg-white/70 px-4 py-3 text-sm text-slate-600 shadow-sm shadow-slate-200/50">
        <span className="font-medium text-slate-900">{summary.total} contact{summary.total === 1 ? '' : 's'}</span>
        <span> · showing {summary.visible}</span>
        {summary.withOpenFollowUps > 0 && (
          <span> · {summary.withOpenFollowUps} with open follow-ups</span>
        )}
        {summary.unknownContacts > 0 && (
          <span> · {summary.unknownContacts} unknown</span>
        )}
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <SectionCard className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-white/70 px-5 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Phonebook</div>
            <p className="mt-1 text-sm text-slate-600">Names, companies, phones, and the latest source-backed touch.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span>Showing {summary.visible} of {summary.total}</span>
            {searchOpen || search.trim() ? (
              <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1">
                <Search className="h-3.5 w-3.5 text-slate-400" />
                <input
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Search contacts…"
                  className="w-36 bg-transparent text-xs text-slate-800 outline-none placeholder:text-slate-400 sm:w-52"
                />
                <button
                  type="button"
                  onClick={closeSearch}
                  className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label={search.trim() ? 'Clear contact search' : 'Close contact search'}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:text-blue-700"
                aria-label="Search contacts"
              >
                <Search className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setViewOptionsOpen(open => !open)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-medium transition ${
                showViewOptions
                  ? 'border-slate-300 bg-slate-900 text-white shadow-sm shadow-slate-200'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:text-blue-700'
              }`}
              aria-expanded={showViewOptions}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              View options
            </button>
          </div>
        </div>

        {showViewOptions && (
          <div className="border-b border-slate-100 bg-slate-50/50 px-5 py-4">
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_190px] lg:items-end">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Follow-ups</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {FOLLOW_UP_FILTERS.map(option => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setFollowUpFilter(option.key)}
                      title={option.help}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        followUpFilter === option.key
                          ? 'border-slate-900 bg-slate-900 text-white shadow-sm shadow-slate-200'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-amber-200 hover:bg-amber-50 hover:text-amber-800'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Sort
                <select
                  value={sort}
                  onChange={event => setSort(event.target.value as ContactDirectorySort)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {SORT_OPTIONS.map(option => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>
            {hasViewState && (
              <button
                type="button"
                onClick={clearFilters}
                className="mt-3 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50"
              >
                Clear view
              </button>
            )}
          </div>
        )}

        {loading && !rows ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">Loading…</div>
        ) : !rows || rows.length === 0 ? (
          <EmptyState />
        ) : visibleRows.length === 0 ? (
          <EmptyState filtered={hasFilters} />
        ) : (
          <ul className="divide-y divide-slate-100 bg-white/40">
            {visibleRows.map(row => (
              <ContactRow key={row.phone} row={row} />
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function ContactRow({ row }: { row: AdminContactListRow }) {
  const displayName = row.name ?? 'Unknown contact';
  const hasOpenFollowUps = row.pendingFollowUps > 0;
  const lastContact = row.lastContactAt ? new Date(row.lastContactAt).toLocaleString() : 'No recent touch';

  return (
    <li>
      <Link
        to={`/contacts/${encodeURIComponent(row.phone)}`}
        className="block px-5 py-4 transition hover:bg-slate-50/80"
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-950">
              <span className={row.name ? '' : 'italic text-slate-500'}>{displayName}</span>
              {hasOpenFollowUps && (
                <StatusBadge tone="amber" size="sm">
                  {row.pendingFollowUps} open follow-up{row.pendingFollowUps === 1 ? '' : 's'}
                </StatusBadge>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              {row.company && <span>{row.company}</span>}
              {row.company && <span aria-hidden="true">•</span>}
              <span className="font-mono">{row.phone}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 md:justify-end">
            <span>Last touch: <span className="font-medium text-slate-700">{lastContact}</span></span>
            <span aria-hidden="true" className="hidden text-slate-300 sm:inline">•</span>
            <span>{row.totalCalls} call{row.totalCalls === 1 ? '' : 's'}</span>
          </div>
        </div>
      </Link>
    </li>
  );
}

function EmptyState({ filtered = false }: { filtered?: boolean }) {
  return (
    <div className="px-5 py-12 text-center">
      <Users className="mx-auto h-8 w-8 text-slate-300" />
      <p className="mt-3 text-sm font-medium text-slate-900">
        {filtered ? 'No contacts match this view' : 'No contacts yet'}
      </p>
      <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
        {filtered
          ? 'Try clearing search or view options.'
          : 'Contacts will appear here automatically after confirmed customer calls.'}
      </p>
    </div>
  );
}
